// src/pages/StudentAchievement.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import colors from "../Constants/colors";

const StudentAchievement = () => {
  const [teacherClassIncharge, setTeacherClassIncharge] = useState("");
  const [assignedClass, setAssignedClass] = useState("");
  const [assignedSection, setAssignedSection] = useState("");
  const [achievements, setAchievements] = useState([]);

  const [eventData, setEventData] = useState({
    eventName: "",
    eventDate: "",
    Place: "",
    schoolRank: "",
    Description: "",
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ================= FETCH ACHIEVEMENTS =================
  const fetchAchievements = async () => {
    const sessionId = localStorage.getItem("currentSessionId");
    if (!sessionId) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/getAchievementsBySession/${sessionId}`
      );
      setAchievements(response.data);
    } catch (error) {
      console.error("Error fetching achievements:", error);
    }
  };

  useEffect(() => {
    fetchAchievements();
  }, []);

  // ================= FETCH TEACHER =================
  useEffect(() => {
    const teacher = JSON.parse(localStorage.getItem("teacher"));
    const teacherId = teacher?._id || teacher?.id;
    const sessionId = localStorage.getItem("currentSessionId");

    if (!teacherId || !sessionId) {
      setMessage("Missing teacher ID or session ID.");
      return;
    }

    const fetchTeacherInfo = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeacherById/${teacherId}`,
        { params: { sessionId } }
      );

      const teacherData = response.data;

      if (teacherData.classIncharge) {
        const [className, sectionName] =
          teacherData.classIncharge.split("-");
        setAssignedClass(className.trim());
        setAssignedSection(sectionName.trim());
        setTeacherClassIncharge(teacherData.classIncharge);
      } else {
        setMessage("You are not in charge of any class.");
      }
    };

    fetchTeacherInfo();
  }, []);

  // ================= INPUT HANDLER =================
  const handleChange = (e) => {
    setEventData({
      ...eventData,
      [e.target.name]: e.target.value,
    });
  };

  // ================= IMAGE HANDLER =================
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file || null);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  // ================= SUBMIT (COPIED LOGIC) =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const sessionId = localStorage.getItem("currentSessionId");
    if (!sessionId) {
      setMessage("Session ID missing.");
      return;
    }

    if (
      !eventData.eventName ||
      !eventData.eventDate ||
      !eventData.Place ||
      !eventData.Description
    ) {
      setMessage("Please fill all required fields.");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("date", eventData.eventDate);
    formData.append("competitionTitle", eventData.eventName);
    formData.append("place", eventData.Place);
    formData.append("schoolRank", eventData.schoolRank); // backend expects it
    formData.append("description", eventData.Description);
    formData.append("sessionId", sessionId);
    formData.append("className", assignedClass);
    formData.append("sectionName", assignedSection);
    if (image) formData.append("image", image);

    try {
      const url = editingId
        ? `${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/updateStudentAchievement/${editingId}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/createStudentAchievement`;

      const method = editingId ? "put" : "post";

      await axios[method](url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage(
        `✅ Achievement ${editingId ? "updated" : "added"} successfully`
      );

      resetForm();
      fetchAchievements();
    } catch (error) {
      console.error("Submit error:", error.response?.data || error);
      setMessage("❌ Failed to submit achievement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ================= EDIT =================
  const handleEdit = (id) => {
    const achievement = achievements.find((a) => a._id === id);
    if (!achievement) return;

    setEventData({
      eventName: achievement.competitionTitle || "",
      eventDate: achievement.date?.split("T")[0] || "",
      Place: achievement.place || "",
      schoolRank: achievement.schoolRank || "",
      Description: achievement.description || "",
    });

    setPreview(achievement.image || null);
    setEditingId(id);
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    const sessionId = localStorage.getItem("currentSessionId");
    if (!window.confirm("Delete this achievement?")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/deleteStudentAchievement/${id}`,
        { params: { sessionId } }
      );
      setMessage("🗑️ Achievement deleted successfully!");
      fetchAchievements();
    } catch (error) {
      console.error("Delete error:", error);
      setMessage("❌ Failed to delete achievement.");
    }
  };

  // ================= RESET =================
  const resetForm = () => {
    setEventData({
      eventName: "",
      eventDate: "",
      Place: "",
      schoolRank: "",
      Description: "",
    });
    setImage(null);
    setPreview(null);
    setEditingId(null);
  };

  const filteredAchievements = achievements.filter(
    (a) => a.className === assignedClass && a.sectionName === assignedSection
  );

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <i className="fas fa-trophy text-yellow-500 !text-[20px]" />
          <h1 className="font-bold !text-[18px]">Student Achievements</h1>
        </div>
      </div>
      <p className="text-gray-600 mb-6 !text-[14px]">
        {teacherClassIncharge
          ? `Class Incharge: ${teacherClassIncharge}`
          : "No class assigned."}
      </p>

      {/* Form Card */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 mb-10">
        <h2 className="font-semibold !text-[18px] text-gray-800 mb-4 text-center md:text-left">
          {editingId
            ? `Edit Achievement for ${assignedClass} (${assignedSection})`
            : `Add Achievement for ${assignedClass} (${assignedSection})`}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Event Date */}
            <div className="flex flex-col">
              <label className="mb-1 font-semibold text-gray-700">Date</label>
              <input
                type="date"
                name="eventDate"
                value={eventData.eventDate}
                onChange={handleChange}
                className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>

            {/* Event Name */}
            <div className="flex flex-col">
              <label className="mb-1 font-semibold text-gray-700">Event Name</label>
              <input
                type="text"
                name="eventName"
                value={eventData.eventName}
                onChange={handleChange}
                placeholder="Enter event name"
                className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>

            {/* Place */}
            <div className="flex flex-col">
              <label className="mb-1 font-semibold text-gray-700">Place</label>
              <input
                type="text"
                name="Place"
                value={eventData.Place}
                onChange={handleChange}
                placeholder="Enter place"
                className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>

            {/* Optional Rank / Extra Field (if you want to keep layout symmetry) */}
            <div className="flex flex-col">
              <label className="mb-1 font-semibold text-gray-700">School Rank</label>
              <input
                type="text"
                name="schoolRank"
                value={eventData.schoolRank}
                onChange={handleChange}
                placeholder="Enter rank"
                className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col">
            <label className="mb-1 font-semibold text-gray-700">Description</label>
            <textarea
              name="Description"
              value={eventData.Description}
              onChange={handleChange}
              rows="4"
              placeholder="Write description..."
              className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              className="px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700"
            >
              {editingId ? "Update Achievement" : "Add Achievement"}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
          </div>

          {message && (
            <p className="text-center text-gray-700 mt-2 text-[14px]">
              {message}
            </p>
          )}
        </form>

      </div>

      {/* Achievements List */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
        <h2 className="font-semibold !text-[18px] text-gray-800 mb-4">
          Achievements List
        </h2>

        {filteredAchievements.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredAchievements.map((achievement) => (
              <div
                key={achievement._id}
                className="border border-gray-200 rounded-lg shadow-sm p-4 hover:shadow-md transition bg-white"
              >
                <h3 className="font-semibold text-gray-900 !text-[16px] mb-1">
                  {achievement.competitionTitle}
                </h3>

                <p className="text-gray-600 !text-[13px] mb-1 flex items-center gap-2">
                  <i className="fas fa-calendar-alt text-indigo-500 !text-[14px]" />
                  {achievement.date
                    ? new Date(achievement.date).toLocaleDateString()
                    : "—"}
                </p>

                <p className="text-gray-700 !text-[14px] mb-1">
                  <strong>Place:</strong> {achievement.place}
                </p>

                <p className="text-gray-700 !text-[14px] mb-1">
                  <strong>Rank:</strong> {achievement.schoolRank}
                </p>

                {achievement.description && (
                  <p className="text-gray-500 !text-[13px] mt-2">
                    {achievement.description}
                  </p>
                )}


                <div className="flex justify-between mt-2 gap-2">
                  <button
                    onClick={() => handleEdit(achievement._id)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-lg !text-[13px]"
                  >
                    <i className="fas fa-edit mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(achievement._id)}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg !text-[13px]"
                  >
                    <i className="fas fa-trash mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center !text-[14px] mt-2">
            No achievements found for this class and section.
          </p>
        )}
      </div>
    </div>
  );
};

export default StudentAchievement;
